import { ChatConversation, ChatMessage } from '@prisma/client'
import { Link, useParams } from '@remix-run/react'
import { Avatar, Badge, Flex, Tag, Typography } from 'antd'
import React from 'react'
import { styled } from 'styled-components'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { DesignSystem } from '~/core/designSystem'
import { OrganizationType } from '~/features/organization/common'
import { OrganizationMemberClientShared } from '~/features/organizationMember/clientShared'
import { Toolbox } from '~/plugins/toolbox/shared'
import { LoaderDots } from '../Thread/components/LoaderDots'

const Wrapper = styled(Flex)<{ $isActive: boolean }>`
  width: 100%;
  overflow: hidden;
  cursor: pointer;
  border-right: 2px solid transparent;

  ${props => {
    if (props.$isActive) {
      return `
        border-right-color: ${DesignSystem.ThemeConst.PRIMARY_YELLOW};
        background: white;
      `
    }

    return `
      &:hover {
        background: rgb(248, 248, 248);
      }
    `
  }}
`

type Props = {
  isActive?: boolean
  isTyping?: boolean
  countNotifications?: number
  organization: OrganizationType
  organizationMember: OrganizationMemberFromContext
  conversation?: ChatConversation
  messageLast?: ChatMessage
  onSelect?: () => void
}

export const ContactBarItem: React.FC<Props> = props => {
  const { isActive, organization, conversation, onSelect } = props

  const isLink = conversation?.id

  if (isLink) {
    return (
      <Wrapper vertical $isActive={isActive}>
        <Link
          className="mrb-select-none"
          to={`/organizations/${organization.id}/chat/${conversation?.id}`}
        >
          <Internal {...props} onSelect={undefined} />
        </Link>
      </Wrapper>
    )
  } else {
    return (
      <Wrapper
        className="mrb-select-none"
        vertical
        onClick={onSelect}
        $isActive={isActive}
      >
        <Internal {...props} />
      </Wrapper>
    )
  }
}

export const Internal: React.FC<Props> = props => {
  const {
    isActive = false,
    isTyping = false,
    countNotifications = 0,
    organization,
    organizationMember,
    messageLast,
    conversation,
  } = props

  const designSystem = DesignSystem.use()
  const params = useParams<{ conversationId: string }>()

  const { token } = designSystem.useToken()

  const isNotRead =
    countNotifications > 0 && conversation?.id !== params.conversationId
  const name = OrganizationMemberClientShared.Helper.getName(organizationMember)
  const role = OrganizationMemberClientShared.Helper.getRole(organizationMember)
  const pictureUrl =
    OrganizationMemberClientShared.Helper.getPictureUrl(organizationMember)

  const agentStatus = organizationMember?.appSession?.status ?? 'ACTIVE'

  const toOnboard = agentStatus === 'TO_ONBOARD'
  const isDisabled = agentStatus === 'DISABLED'

  const date = messageLast
    ? Toolbox.Date.toWhatsappStyle(messageLast.createdAt)
    : null

  const contentPreview = getContentPreview(props)

  const dateColor = isNotRead ? token.colorPrimary : undefined

  return (
    <Flex className="w-full px-3 py-2" vertical>
      <Flex gap={8} align="center" style={{ overflow: 'hidden' }}>
        <Flex>
          <Avatar src={pictureUrl} size={46} shape="square" />
        </Flex>

        <Flex flex={1} vertical style={{ overflow: 'hidden' }}>
          <Flex>
            <Flex flex={1} align="center" gap={2}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {role}
              </Typography.Title>
            </Flex>
            <Typography.Text
              type="secondary"
              style={{
                color: dateColor,
                fontSize: '12px',
                fontWeight: isNotRead ? 600 : undefined,
              }}
            >
              {date}
            </Typography.Text>
          </Flex>

          <Flex align="center" style={{ height: '20px' }}>
            {isTyping && (
              <Flex flex={1}>
                <LoaderDots />
              </Flex>
            )}

            {!isTyping && isDisabled && (
              <Flex>
                <Badge
                  count={'✈️'}
                  styles={{
                    indicator: {
                      background: 'transparent',
                      borderColor: 'transparent',
                    },
                  }}
                />
              </Flex>
            )}

            <Flex flex={1} style={{ overflow: 'hidden' }}>
              {!isTyping && (
                <Typography.Text
                  type={isNotRead || isActive ? undefined : 'secondary'}
                  ellipsis
                  style={{
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontWeight: isNotRead ? 600 : undefined,
                    fontStyle: isDisabled ? 'italic' : undefined,
                  }}
                >
                  {name}: {contentPreview}
                </Typography.Text>
              )}
            </Flex>

            {isNotRead && (
              <Flex vertical>
                <Tag
                  color="red-inverse"
                  style={{
                    margin: 0,
                    borderRadius: '10px',
                    fontSize: '11px',
                    lineHeight: '15px',
                  }}
                >
                  {countNotifications}
                </Tag>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}

const getContentPreview = ({ messageLast, organizationMember }: Props) => {
  const agentStatus = organizationMember?.appSession?.status ?? 'ACTIVE'
  const isDisabled = agentStatus === 'DISABLED'
  const contentPreview = messageLast?.content

  if (isDisabled) {
    return `Taking a short break`
  }

  return contentPreview ?? '👋'
}
